import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useQueryClient } from 'react-query';
import { getUserById } from '../services/userApi';
import { searchUserByEmail } from '../services/userApi'; // Import searchUserByEmail

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// --- Centralized Launch Control ---
const LAUNCH_DATE = new Date('2026-01-13T09:00:00Z'); // Launch date set to Jan 22, 2026, in UTC
const isAppLaunched = () => {
  // Allow bypassing the launch date for testing purposes via an environment variable
  if (process.env.REACT_APP_BYPASS_LAUNCH_GATE === 'true') {
    return true;
  }
  return new Date() >= LAUNCH_DATE;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Check if authentication is disabled for local development
  const isAuthEnabled = process.env.REACT_APP_AUTH_ENABLED !== 'false';
  const isTestUserMode = process.env.REACT_APP_TEST_USER_MODE === 'true';
  const testUserId = process.env.REACT_APP_TEST_USER_ID || 'test-user-123';

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTestUserName = async () => {
    try {
      const profileData = await getUserById(testUserId);
      return profileData?.fullName || process.env.REACT_APP_TEST_USER_NAME || 'Test User';
    } catch (e) {
      console.error("Failed to fetch test user profile", e);
      return process.env.REACT_APP_TEST_USER_NAME || 'Test User';
    }
  };

  const checkAuthStatus = async () => {
    // If the app hasn't launched, refuse to authenticate anyone.
    if (!isAppLaunched()) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      localStorage.removeItem('custom_session');
      return;
    }

    let authenticated = false;
    let userToSet = null;

    try {
      const customSessionData = localStorage.getItem('custom_session');
      if (customSessionData) {
        try {
          const session = JSON.parse(customSessionData);
          userToSet = session.user;
          authenticated = true;
        } catch (e) {
          console.error("Failed to parse custom session", e);
          localStorage.removeItem('custom_session');
        }
      }

      if (!authenticated && sessionStorage.getItem('hasSignedOut') === 'true' && !isAuthEnabled) {
        // Handled in finally
      } else if (!authenticated && (!isAuthEnabled || isTestUserMode) && sessionStorage.getItem('isLoggedIn') === 'true') {
        // Use test user in local development
        const profileName = await getTestUserName();
        // Fetch full user profile from custom backend to get the role
        const customUser = await searchUserByEmail(process.env.REACT_APP_TEST_USER_EMAIL || 'test@saisruthi.com');
        let userRole = 'normal'; // Default role for test user
        if (customUser && customUser.length > 0) {
          userRole = customUser[0].role;
        } else if (process.env.REACT_APP_TEST_USER_GROUPS?.includes('Admins')) {
          userRole = 'admin'; // Fallback for test admin
        }
        userToSet = {
          sub: testUserId,
          name: profileName,
          email: process.env.REACT_APP_TEST_USER_EMAIL || 'test@saisruthi.com',
          phone_number: process.env.REACT_APP_TEST_USER_PHONE || '+911234567890',
          attributes: {
            name: profileName,
            email: process.env.REACT_APP_TEST_USER_EMAIL || 'test@saisruthi.com',
            phone_number: process.env.REACT_APP_TEST_USER_PHONE || '+911234567890',
          },
          role: userRole, // Set the role here
        };
        authenticated = true;
      } else if (!authenticated && isAuthEnabled) {
        const currentUser = await Auth.currentAuthenticatedUser(); // Cognito user object
        // Fetch full user profile from custom backend to get the role
        const customUser = await searchUserByEmail(currentUser.attributes.email);
        if (customUser && customUser.length > 0) {
          userToSet = {
            ...currentUser,
            role: customUser[0].role, // Add role from custom backend
            userId: customUser[0].userId, // Ensure userId is also present for consistency
          };
        }
        userToSet = currentUser;
        authenticated = true;
      }
    } catch (error) {
      console.log('No authenticated user:', error);
      userToSet = null;
      authenticated = false;
    } finally {
      setUser(userToSet);
      setIsAuthenticated(authenticated);
      setLoading(false);
    }
  };

  const isUserProfileIncomplete = (user) => {
    // Check if user object or its attributes are missing 'name'
    return !user?.attributes?.name;
  };

  const signIn = async (email, password) => {
    if (!isAuthEnabled) {
      // Simulate successful login in test mode
      sessionStorage.removeItem('hasSignedOut');
      sessionStorage.setItem('isLoggedIn', 'true');
      // Fetch full user profile from custom backend to get the role
      const customUser = await searchUserByEmail(email);
      let userRole = 'normal';
      let userIdFromCustom = testUserId;
      if (customUser && customUser.length > 0) {
        userRole = customUser[0].role;
        userIdFromCustom = customUser[0].userId;
      }
      const profileName = customUser?.[0]?.fullName || await getTestUserName();
      const testUser = {
        sub: userIdFromCustom,
        userId: userIdFromCustom, // Ensure userId is also present
        name: profileName,
        email: email,
        attributes: { name: profileName, email: email },
        phone_number: process.env.REACT_APP_TEST_USER_PHONE || '+911234567890',
        role: userRole, // Set the role here
      };
      setUser(testUser);
      setIsAuthenticated(true);
      return { user: testUser, isNewUser: isUserProfileIncomplete(testUser) };
    }

    try {
      await Auth.signIn(email, password);
      const cognitoUser = await Auth.currentAuthenticatedUser(); // Cognito user object
      // Fetch full user profile from custom backend to get the role
      const customUser = await searchUserByEmail(email);
      let userWithRole = cognitoUser;
      if (customUser && customUser.length > 0) {
        userWithRole = {
          ...cognitoUser,
          role: customUser[0].role, // Add role from custom backend
          userId: customUser[0].userId, // Ensure userId is also present
        };
      }
      setUser(userWithRole);
      setIsAuthenticated(true);
      return { user: userWithRole, isNewUser: isUserProfileIncomplete(userWithRole) };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signUp = async (email, password, attributes) => {
    if (!isAuthEnabled) {
      // Simulate successful signup in test mode
      return { userSub: testUserId };
    }

    try {
      const result = await Auth.signUp({
        username: email,
        password,
        attributes
      });
      return result;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    if (!isAuthEnabled) {
      sessionStorage.setItem('hasSignedOut', 'true');
      sessionStorage.removeItem('isLoggedIn');
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('custom_session');
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
      return;
    }

    try {
      await Auth.signOut();
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('custom_session');
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const sendOTP = async (phoneNumber) => {
    if (!isAuthEnabled) {
      // Simulate OTP send
      console.log('OTP sent to:', phoneNumber);
      return { success: true };
    }

    try {
      // For custom auth flow with OTP
      const user = await Auth.signIn(phoneNumber);
      return user;
    } catch (error) {
      console.error('Send OTP error:', error);
      throw error;
    }
  };

  const verifyOTP = async (phoneNumber, otp) => {
    if (!isAuthEnabled) {
      // Simulate OTP verification - accept any 6-digit code
      if (otp && otp.length === 6) {
        sessionStorage.setItem('isLoggedIn', 'true');
        const profileName = await getTestUserName();
        const testUser = {
          sub: testUserId,
          name: profileName,
          phone_number: phoneNumber,
          attributes: { name: profileName, phone_number: phoneNumber }
        };
        setUser(testUser);
        setIsAuthenticated(true);
        return { user: testUser, isNewUser: isUserProfileIncomplete(testUser) };
      } else {
        throw new Error('Invalid OTP');
      }
    }

    try {
      await Auth.sendCustomChallengeAnswer(phoneNumber, otp);
      const fullUser = await Auth.currentAuthenticatedUser(); // Fetch full user attributes after verification
      setUser(fullUser);
      setIsAuthenticated(true);
      return { user: fullUser, isNewUser: isUserProfileIncomplete(fullUser) };
    } catch (error) {
      console.error('Verify OTP error:', error);
      throw error;
    }
  };

  const socialSignIn = async (provider) => {
    if (!isAuthEnabled) {
      // Simulate social sign in
      sessionStorage.setItem('isLoggedIn', 'true');
      const profileName = await getTestUserName();
      const testUser = {
        sub: testUserId,
        name: profileName,
        email: `test-${provider}@saisruthi.com`,
        phone_number: process.env.REACT_APP_TEST_USER_PHONE || '+911234567890', // Ensure phone_number is present
        attributes: { 
          name: profileName, 
          email: `test-${provider}@saisruthi.com`,
          phone_number: process.env.REACT_APP_TEST_USER_PHONE || '+911234567890'
        }
      };
      setUser(testUser);
      setIsAuthenticated(true);
      return { user: testUser, isNewUser: isUserProfileIncomplete(testUser) };
    }

    try {
      await Auth.federatedSignIn({ provider });
      const fullUser = await Auth.currentAuthenticatedUser(); // Fetch full user attributes after sign in
      return { user: fullUser, isNewUser: isUserProfileIncomplete(fullUser) };
    } catch (error) {
      console.error('Social sign in error:', error);
      throw error;
    }
  };

  const updateUserAttributes = async (attributes) => {
    if (!isAuthEnabled) {
      // Simulate attribute update for test user
      setUser(prevUser => ({
        ...prevUser,
        attributes: { ...prevUser.attributes, ...attributes },
        ...attributes // Also update top-level for convenience if needed
      }));
      return;
    }

    try {
      const result = await Auth.updateUserAttributes(user, attributes);
      return result;
    } catch (error) {
      console.error('Error updating user attributes:', error);
      throw error;
    }
  };

  const setUserSession = (sessionData) => {
    // The custom login API may not return a token, but the LoginPage
    // constructs a session object with a `user` property. The presence
    // of this `user` object is the correct signal of a successful login.
    if (!isAppLaunched()) {
      console.warn(
        'Login blocked: The application has not launched yet.'
      );
      return;
    }

    if (sessionData && sessionData.user) {
      localStorage.setItem('custom_session', JSON.stringify(sessionData));
      setUser(sessionData.user); // Assuming sessionData contains user info
      setIsAuthenticated(true);
    }
  };

  const cognitoGroups =
user?.signInUserSession?.idToken?.payload?.['cognito:groups'] || [];
const testGroups = (process.env.REACT_APP_TEST_USER_GROUPS || '')
  .split(',')
  .map(s => s.trim())
  .filter(Boolean);

const effectiveGroups =
process.env.REACT_APP_AUTH_ENABLED === 'true' ? cognitoGroups : testGroups;

const isAdmin = (Array.isArray(effectiveGroups) && effectiveGroups.includes('Admins')) || user?.role === 'admin' || user?.role === 'Admin'; // Added 'Admin' for case insensitivity

  const value = {
    isAuthenticated,
    user,
    loading,
       isAdmin ,
    signIn,
    signUp,
    signOut,
    sendOTP,
    verifyOTP,
    socialSignIn,
    checkAuthStatus,
    isAuthEnabled,
    isTestUserMode,
    updateUserAttributes,
    isUserProfileIncomplete,
    setUserSession,
    isAppLaunched: isAppLaunched(), // Expose the launch status to the rest of the app
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
